<template>
    <div class="validator-defi-leaderboard">
        <div class="section-divider">
            <h2 class="section-title">DeFi Activity Leaderboard</h2>
            <p class="section-subtitle">Top validators by DeFi trading activity</p>
        </div>

        <!-- Controls -->
        <div class="d-flex gap-3 align-items-center flex-wrap mb-4">
            <div class="btn-group">
                <button
                    v-for="opt in sortOptions"
                    :key="opt.value"
                    class="compass-btn"
                    :class="{ 'compass-btn--primary': sortBy === opt.value, 'compass-btn--outline': sortBy !== opt.value }"
                    @click="sortBy = opt.value"
                >
                    {{ opt.label }}
                </button>
            </div>
            <div class="ms-auto">
                <date-range-picker default-range="7d" @change="handleRangeChange" />
            </div>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <!-- Leaderboard Table -->
        <div class="analytics-panel" v-if="!loading">
            <div class="panel-body">
                <div class="table-responsive">
                    <table class="compass-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Validator</th>
                                <th class="text-end">Blocks</th>
                                <th class="text-end">Swap Volume</th>
                                <th class="text-end">Traders</th>
                                <th class="text-end">Completion</th>
                                <th class="text-end">Revenue</th>
                                <th class="text-end">DeFi Score</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(v, idx) in validators" :key="v.leader">
                                <td>{{ idx + 1 }}</td>
                                <td>
                                    <a :href="`/v/${v.leader}`" class="text-gradient">
                                        {{ formatPubkey(v.leader) }}
                                    </a>
                                    <span class="badge bg-secondary ms-2" v-if="v.blockShare > 1">
                                        {{ v.blockShare.toFixed(1) }}% blocks
                                    </span>
                                </td>
                                <td class="text-end">{{ formatCompact(v.blocks) }}</td>
                                <td class="text-end">
                                    <span class="text-gradient">{{ formatUsd(v.swapVolume) }}</span>
                                    <br>
                                    <small class="text-muted">{{ v.swapVolumeShare.toFixed(2) }}%</small>
                                </td>
                                <td class="text-end">
                                    {{ formatCompact(v.traders) }}
                                    <br>
                                    <small class="text-muted">{{ v.traderShare.toFixed(2) }}%</small>
                                </td>
                                <td class="text-end">
                                    <span :class="getCompletionClass(v.completionRate)">
                                        {{ v.completionRate }}%
                                    </span>
                                </td>
                                <td class="text-end">{{ formatSOL(v.revenue) }}</td>
                                <td class="text-end">
                                    <div class="defi-score-badge" :class="getScoreClass(v.defiExposureScore)">
                                        {{ v.defiExposureScore }}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Network Summary -->
        <div class="analytics-row cols-3 mt-4" v-if="network">
            <div class="analytics-panel analytics-panel--compact">
                <div class="panel-body text-center py-3">
                    <div class="hero-value">{{ formatUsd(network.totalSwapVolume) }}</div>
                    <div class="panel-subtitle mt-1">Network Swap Volume</div>
                </div>
            </div>
            <div class="analytics-panel analytics-panel--compact">
                <div class="panel-body text-center py-3">
                    <div class="hero-value" style="background: linear-gradient(135deg, #22c55e, #10f7cf); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">{{ formatCompact(network.totalTraders) }}</div>
                    <div class="panel-subtitle mt-1">Total Traders</div>
                </div>
            </div>
            <div class="analytics-panel analytics-panel--compact">
                <div class="panel-body text-center py-3">
                    <div class="hero-value" style="background: linear-gradient(135deg, #3b82f6, #6366f1); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">{{ formatCompact(network.totalBlocks) }}</div>
                    <div class="panel-subtitle mt-1">Total Blocks</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ValidatorDefiLeaderboard',

    data() {
        return {
            loading: true,
            validators: [],
            network: null,
            range: '7d',
            sortBy: 'swap_volume',
            sortOptions: [
                { value: 'swap_volume', label: 'Swap Volume' },
                { value: 'traders', label: 'Traders' },
                { value: 'fees', label: 'Fees' },
                { value: 'jito', label: 'Jito Tips' }
            ]
        };
    },

    mounted() {
        this.loadData();
    },

    watch: {
        sortBy() {
            this.loadData();
        }
    },

    methods: {
        handleRangeChange(data) {
            this.range = data.range;
            this.loadData();
        },

        async loadData() {
            this.loading = true;

            try {
                const res = await fetch(`/analytics/api/validator-defi-leaderboard?range=${this.range}&sort=${this.sortBy}&limit=50`);
                const data = await res.json();
                this.validators = data.validators || [];
                this.network = data.network || null;
            } catch (e) {
                console.error('Error loading validator leaderboard:', e);
            } finally {
                this.loading = false;
            }
        },

        formatCompact(v) {
            if (!v) return '0';
            if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B';
            if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M';
            if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
            return v.toLocaleString();
        },
        formatUsd(v) {
            if (!v) return '$0';
            if (v >= 1e9) return '$' + (v / 1e9).toFixed(2) + 'B';
            if (v >= 1e6) return '$' + (v / 1e6).toFixed(2) + 'M';
            if (v >= 1e3) return '$' + (v / 1e3).toFixed(1) + 'K';
            return '$' + v.toFixed(2);
        },
        formatSOL(v) {
            if (!v) return '0 SOL';
            if (v >= 1000) return (v / 1000).toFixed(1) + 'K SOL';
            return v.toFixed(2) + ' SOL';
        },
        formatPubkey(a) {
            return a && a.length > 12 ? `${a.slice(0, 4)}..${a.slice(-4)}` : a;
        },
        getCompletionClass(rate) {
            if (rate >= 50) return 'text-success';
            if (rate >= 25) return 'text-warning';
            return 'text-danger';
        },
        getScoreClass(score) {
            if (score >= 80) return 'score-high';
            if (score >= 50) return 'score-medium';
            return 'score-low';
        }
    }
};
</script>

<style scoped>
.defi-score-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-weight: 600;
    font-size: 0.875rem;
}

.score-high {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(16, 247, 207, 0.2));
    color: var(--compass-cyan);
}

.score-medium {
    background: rgba(245, 158, 11, 0.2);
    color: #f59e0b;
}

.score-low {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
}

.text-success { color: var(--compass-success, #22c55e); }
.text-warning { color: #f59e0b; }
.text-danger { color: var(--compass-danger, #ef4444); }
</style>
